import { useState, useEffect, useRef } from 'react';
import { InputTextCustom, Combobox, Modal, InputDate, InputPhone, initialStatePhoneNumber, mobilePhoneToString, validMobileForInputPhone } from 'medere-ui-framework';
import { Icon } from '@iconify/react';
import { HealthPatientCoverage, defaultHealthPatientCoverage, defaultHealthEntity, defaultHealthEntityPlan, defaultNewPatientRecordDTO, HealthEntityPlan, HealthEntity, PatientRecordDTO } from '../../interface/interface';
import { Button } from 'primereact/button';

import { validateInternationalCellphone } from '../../utils/utils';
import { Toast, ToastMessage } from 'primereact/toast';
import './PatientForm.scss';
import { getAllHealthEntities, getAllHealthEntityPlanByHealthEntityId, isGenderNull } from '../../servicesHooks/DigitalRP/useDigitalRPService';
import CustomCheckBox from '../CustomCheckBox/CustomCheckBox';

export interface PatientFormProps {
  /** Initial patient data to pre-fill the form. null = new patient */
  initialPatient: PatientRecordDTO | null;
  /** Called when the form data changes. Receives the current form state. */
  onPatientChange: (patient: PatientRecordDTO) => void;
  /** Called when form validity changes */
  onValidityChange?: (isValid: boolean) => void;
  /** If true, hides the title header */
  hideTitle?: boolean;
  /** If true, hides the observations field */
  hideObservations?: boolean;
}

const genderOptions = [
  { id: 'M', name: 'Masculino' },
  { id: 'F', name: 'Femenino' }
];

function generateDate(date: string) {
  const [year, month, auxDay] = date.split('-');
  const day = auxDay.split('T')[0];
  const formattedMonth = month.length < 2 ? `0${month}` : month;
  const formattedDay = day.length < 2 ? `0${day}` : day;
  const formattedDate = `${year}-${formattedMonth}-${formattedDay}T03:00:00.000Z`;
  const finalDate = new Date(formattedDate);
  const comparableDay = day < '10' ? day.slice(1, 2) : day;
  if (finalDate.getDate().toString() < comparableDay && (finalDate.getMonth() + 1).toString() <= month && finalDate.getFullYear().toString() === year) {
    finalDate.setDate(finalDate.getDate() + 1);
  }
  if (finalDate.getDate().toString() > comparableDay && (finalDate.getMonth() + 1).toString() >= month && finalDate.getFullYear().toString() === year) {
    finalDate.setDate(finalDate.getDate() - 1);
  }
  if (finalDate.getFullYear().toString() < year) {
    finalDate.setDate(finalDate.getDate() + 1);
  }
  if (finalDate.getFullYear().toString() > year) {
    finalDate.setDate(finalDate.getDate() - 1);
  }
  return finalDate;
}

export default function PatientForm({
  initialPatient,
  onPatientChange,
  onValidityChange,
  hideTitle = false,
  hideObservations = false,
}: PatientFormProps) {
  const [newPatient, setNewPatient] = useState<PatientRecordDTO>(initialPatient ?? defaultNewPatientRecordDTO);
  const [birthdate, setBirthdate] = useState<Date | null>(
    initialPatient?.birthdate ? generateDate(initialPatient.birthdate.toString()) : null
  );
  const [insuranceModalVisible, setInsuranceModalVisible] = useState(false);
  const [insuranceToEdit, setInsuranceToEdit] = useState<HealthPatientCoverage | null>(null);
  const [selectedCoverage, setSelectedCoverage] = useState<HealthPatientCoverage | null>(defaultHealthPatientCoverage);
  const [selectedHealthEntity, setSelectedHealthEntity] = useState<HealthEntity>(defaultHealthEntity);
  const [selectedHealthEntityPlan, setSelectedHealthEntityPlan] = useState<HealthEntityPlan>(defaultHealthEntityPlan);
  const [validationInsuranceModal, setValidationInsuranceModal] = useState(false);
  const [mandatory, setMandatory] = useState(false);
  const [primaryCoverage, setPrimaryCoverage] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openEditing, setOpenEditing] = useState(false);
  const [affiliateError, setAffiliateError] = useState(false);

  const [insurances, setInsurances] = useState<any[]>(initialPatient?.insurances ?? []);
  const [insurancesToDelete, setInsurancesToDelete] = useState<number[]>([]);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 700);
  const [gender, setGender] = useState(initialPatient ? (initialPatient.gender === 'M' ? 'Masculino' : initialPatient.gender === 'F' ? 'Femenino' : null) : null);
  const [patientPhone, setPatientPhone] = useState(validMobileForInputPhone(initialPatient?.mobilephone ?? ''));

  const toast = useRef<Toast>(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check gender null for existing patients
  useEffect(() => {
    if (initialPatient?.medereentity && initialPatient.medereentity > 0) {
      isGenderNull(initialPatient.medereentity).then((genderNull) => {
        if (genderNull) setGender(null);
      }).catch(() => {});
    }
  }, []);

  // Notify parent of patient changes
  useEffect(() => {
    onPatientChange({ ...newPatient, insurances, insurancesToDelete });
  }, [newPatient, insurances, insurancesToDelete]);

  // Validate and notify parent of validity changes
  useEffect(() => {
    const isValid = !!(newPatient?.firstname && newPatient?.lastname && newPatient?.document && newPatient?.gender && newPatient?.birthdate);
    onValidityChange?.(isValid);
  }, [newPatient]);

  // Insurance modal effects
  useEffect(() => {
    validateInsuranceModal();
    if (saving && selectedCoverage?.healthentityplan) {
      if (insuranceToEdit) {
        const updatedInsurances = insurances.map((insurance: HealthPatientCoverage) =>
          insurance === insuranceToEdit ? selectedCoverage : insurance
        );
        setInsurances(updatedInsurances);
      } else {
        setInsurances([...insurances, selectedCoverage]);
      }
      setInsuranceModalVisible(false);
      setInsuranceToEdit(undefined as any);
      setSaving(false);
    }
  }, [selectedCoverage]);

  useEffect(() => {
    if (!insuranceModalVisible) {
      setInsuranceToEdit(undefined as any);
      setSelectedCoverage(defaultHealthPatientCoverage);
      setSelectedHealthEntity(defaultHealthEntity);
      setSelectedHealthEntityPlan(defaultHealthEntityPlan);
      setMandatory(false);
      setPrimaryCoverage(true);
      setAffiliateError(false);
    }
  }, [insuranceModalVisible]);

  useEffect(() => {
    if (!openEditing) setSelectedHealthEntityPlan(defaultHealthEntityPlan);
    else setOpenEditing(false);
    if (!selectedHealthEntity) setSelectedHealthEntity(defaultHealthEntity);
  }, [selectedHealthEntity]);

  useEffect(() => {
    if (selectedCoverage?.benefitnumber) {
      setAffiliateError(!testBenefitNumber());
    }
  }, [selectedCoverage?.benefitnumber]);

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setNewPatient({ ...newPatient, [name]: value });
  };

  const validateInsuranceModal = () => {
    if (!selectedCoverage) { setValidationInsuranceModal(false); return; }
    if (!selectedHealthEntity?.commercialname || !selectedHealthEntityPlan?.name || !selectedCoverage?.benefitnumber || !testBenefitNumber()) {
      setValidationInsuranceModal(false); return;
    }
    setValidationInsuranceModal(true);
  };

  const testBenefitNumber = () => /^[A-Za-z0-9]*$/.test(selectedCoverage?.benefitnumber ?? '');

  const handleInsuranceModalSave = () => {
    if (primaryCoverage) {
      const updatedInsurances = insurances.map(insurance => {
        if (insuranceToEdit && insurance.healthentityplan.healthentity.healthentity === insuranceToEdit.healthentityplan.healthentity.healthentity)
          return insurance;
        else return { ...insurance, primarycoverage: false };
      });
      setInsurances(updatedInsurances);
    }
    const healthentityplan: HealthEntityPlan = { healthentityplan: selectedHealthEntityPlan.healthentityplan, name: selectedHealthEntityPlan.name, healthentity: selectedHealthEntity };
    setSelectedCoverage({
      ...selectedCoverage,
      healthentityplan,
      voluntary: !mandatory,
      primarycoverage: primaryCoverage,
      healthpatientcoverage: selectedCoverage?.healthpatientcoverage ?? 0,
      benefitnumber: selectedCoverage?.benefitnumber ?? '',
      coveragestart: selectedCoverage?.coveragestart ?? new Date(),
      coverageend: selectedCoverage?.coverageend ?? new Date(),
      patient: selectedCoverage?.patient ?? 0,
      description: selectedCoverage?.description ?? '',
    });
  };

  const handleEditInsurance = (insurance: HealthPatientCoverage) => {
    setSelectedHealthEntity({ healthentity: insurance.healthentityplan.healthentity.healthentity, commercialname: insurance.healthentityplan.healthentity.commercialname, recipevalidatorid: insurance.healthentityplan.healthentity.recipevalidatorid });
    setSelectedHealthEntityPlan({ healthentityplan: insurance.healthentityplan.healthentityplan, name: insurance.healthentityplan.name, healthentity: selectedHealthEntity });
    setInsuranceToEdit(insurance);
    setPrimaryCoverage(insurance.primarycoverage);
    setSelectedCoverage(insurance);
    setMandatory(!insurance.voluntary);
    setOpenEditing(true);
    setInsuranceModalVisible(true);
  };

  const handleDeleteInsurance = (insurance: HealthPatientCoverage) => {
    if (insurance.healthpatientcoverage)
      setInsurancesToDelete([...insurancesToDelete, insurance.healthpatientcoverage]);
    setInsurances(insurances.filter((i) => i !== insurance));
  };

  return (
    <div className="patient-form">
      {!hideTitle && (
        <div className="patient-form__title">
          <h2><strong>{initialPatient ? 'Editar' : 'Crear'}</strong> paciente</h2>
        </div>
      )}

      <div className="patient-form__fields">
        <div className="patient-form__row">
          <div className="patient-form__field">
            <label>Nombre</label>
            <InputTextCustom className="border2" name="firstname" value={newPatient?.firstname} onChange={handleInputChange} />
          </div>
          <div className="patient-form__field">
            <label>Apellido</label>
            <InputTextCustom className="border2" name="lastname" value={newPatient?.lastname} onChange={handleInputChange} />
          </div>
        </div>

        <div className="patient-form__row patient-form__row--3">
          <div className="patient-form__field">
            <label>DNI</label>
            <InputTextCustom className="border2" name="document" value={newPatient?.document} onChange={handleInputChange} />
          </div>
          <div className="patient-form__field">
            <label>Sexo</label>
            <Combobox
              className="border2"
              getItems={() => Promise.resolve(genderOptions)}
              optionLabel="name"
              value={genderOptions.find((option) => option.name === gender)}
              setValue={(e: any) => { setGender(e.name); setNewPatient({ ...newPatient, gender: e.id }); }}
            />
          </div>
          <div className="patient-form__field">
            <label>Fecha de Nacimiento</label>
            <InputDate
              className="border2"
              name="birthdate"
              value={birthdate}
              onChange={(e: any) => { setNewPatient({ ...newPatient, birthdate: e.target.value }); setBirthdate(e.target.value); }}
              maxDate={new Date()}
            />
          </div>
        </div>

        <div className="patient-form__row">
          <div className="patient-form__field">
            <InputPhone
              cboClassname="border2 w-4"
              inputClassname="border2"
              value={patientPhone}
              label="Celular. Característica sin 0 y número sin 15. (Ejemplo: 2231234567)"
              floatLabel={false}
              setValue={(mobile: any) => { setPatientPhone(mobile); setNewPatient({ ...newPatient, mobilephone: mobilePhoneToString(mobile) }); }}
            />
          </div>
          <div className="patient-form__field">
            <label>Mail</label>
            <InputTextCustom className="border2" name="email" value={newPatient?.email} onChange={handleInputChange} />
          </div>
        </div>
      </div>

      {/* Coverage section */}
      <div className="patient-form__coverage">
        <div className="patient-form__coverage-header">
          <span className="patient-form__coverage-title">COBERTURA MÉDICA</span>
          <Button
            className="w-auto"
            label="Agregar"
            onClick={() => setInsuranceModalVisible(true)}
            style={{ backgroundColor: '#FFFFFF', color: '#545454', height: '36px', borderColor: '#3CB8BA', minWidth: isMobile ? '100%' : '200px' }}
          />
        </div>
        {insurances.map((insurance: HealthPatientCoverage, index: number) => (
          <div key={index} className="patient-form__insurance-item" style={{ borderBottom: index !== insurances.length - 1 ? '0.1px solid #A9B1BC' : '' }}>
            <span>{insurance?.healthentityplan?.healthentity?.commercialname}</span>
            <div>
              <Button style={{ background: 'none', border: 'none', padding: '0px' }} onClick={() => handleEditInsurance(insurance)}>
                <Icon icon="bi:pencil-square" style={{ color: '#3CB8BA', fontSize: '17px' }} />
              </Button>
              <Button style={{ color: '#3CB8BA', background: 'none', border: 'none', padding: '0px', marginLeft: '5px' }} onClick={() => handleDeleteInsurance(insurance)}>
                <Icon icon="bi:x" style={{ color: '#3CB8BA', fontSize: '25px' }} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {!hideObservations && (
        <div className="patient-form__observations">
          <span className="patient-form__coverage-title">OBSERVACIONES</span>
          <InputTextCustom className="border2" name="observations" value={newPatient?.observations} onChange={handleInputChange} />
        </div>
      )}

      {/* Insurance modal */}
      <Modal
        visible={insuranceModalVisible}
        setVisible={setInsuranceModalVisible}
        header={<label><strong>{insuranceToEdit ? 'Editar' : ''} Cobertura</strong> médica</label>}
        headerStyle={{ fontSize: '12px' }}
        className="h-full"
      >
        <div className="patient-form__insurance-modal">
          <div style={{ overflowY: 'auto', paddingTop: '10px' }}>
            <label>Obra Social</label>
            <Combobox className="border2 mb-4" getItems={getAllHealthEntities} optionLabel="commercialname" value={selectedHealthEntity} setValue={setSelectedHealthEntity} />
            <label>Plan</label>
            <Combobox className="border2 mb-4" getItems={getAllHealthEntityPlanByHealthEntityId} params={{ healthEntityId: selectedHealthEntity?.healthentity }} optionLabel="name" value={selectedHealthEntityPlan} setValue={setSelectedHealthEntityPlan} disable={selectedHealthEntity?.healthentity <= 0} />
            <label>N° de afiliado</label>
            <InputTextCustom
              className={affiliateError ? '' : 'border2'}
              error={affiliateError}
              caption={affiliateError ? 'El n° de afiliado solo puede contener letras y/o números' : ''}
              value={selectedCoverage ? selectedCoverage.benefitnumber : ''}
              onChange={(e: any) => {
                setSelectedCoverage({ ...selectedCoverage, benefitnumber: e.target.value, healthpatientcoverage: selectedCoverage?.healthpatientcoverage ?? 0, coveragestart: selectedCoverage?.coveragestart ?? new Date(), coverageend: selectedCoverage?.coverageend ?? new Date(), primarycoverage: selectedCoverage?.primarycoverage ?? false, voluntary: selectedCoverage?.voluntary ?? false, healthentityplan: selectedCoverage?.healthentityplan ?? defaultHealthEntityPlan, patient: selectedCoverage?.patient ?? 0, description: selectedCoverage?.description ?? '' });
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{ marginBottom: '0.5rem' }}>
                <CustomCheckBox checked={primaryCoverage} onChange={(e: any) => setPrimaryCoverage(e.target.checked ?? false)} />
                <span style={{ fontSize: '15px' }}>Usar esta Obra social como favorita.</span>
              </label>
              <label>
                <CustomCheckBox checked={mandatory} onChange={(e: any) => setMandatory(e.target.checked ?? false)} />
                <span style={{ fontSize: '15px' }}>La cobertura es a través de su trabajo</span>
              </label>
            </div>
          </div>
          <div style={{ paddingTop: '5px', display: 'flex', flexDirection: 'column' }}>
            <Button
              label={insuranceToEdit ? 'Guardar cambios' : 'Agregar cobertura'}
              onClick={() => { setSaving(true); handleInsuranceModalSave(); }}
              disabled={!validationInsuranceModal}
              style={{ marginBottom: '15px', backgroundColor: validationInsuranceModal ? '#14C7BC' : '#9E9E9E', border: 'none', height: '36px' }}
            />
            <Button
              label="Cancelar"
              onClick={() => { setInsuranceModalVisible(false); setInsuranceToEdit(undefined as any); setSelectedCoverage(defaultHealthPatientCoverage); }}
              style={{ backgroundColor: '#FFFFFF', color: '#545454', height: '36px', borderColor: '#3CB8BA' }}
            />
          </div>
        </div>
      </Modal>

      <Toast ref={toast} position="top-right" />
    </div>
  );
}
